import { DBAdapter } from '@powersync/common';
import { Mutex, Semaphore } from '@powersync/shared-internals';
import { acquireFromPool } from './acquireFromPool.js';
/**
 * A connection pool implementation delegating to another pool opened asynchronnously.
 */
export class AsyncDbAdapter extends DBAdapter {
    name;
    state;
    resolvedWriter;
    pendingListeners = new Set();
    constructor(inner, name) {
        super();
        this.name = name;
        this.state = inner.then((client) => {
            for (const pending of this.pendingListeners) {
                pending.closeAfterRegisteredOnResolvedPool = client.writer.registerListener(pending.listener);
            }
            this.pendingListeners.clear();
            this.resolvedWriter = client.writer;
            if (client.additionalReaders.length) {
                return readWritePoolState(client.writer, client.additionalReaders);
            }
            return singleConnectionPoolState(client.writer);
        });
    }
    async init() {
        await this.state;
    }
    async close() {
        const state = await this.state;
        await state.close();
    }
    async readLock(fn, options) {
        const state = await this.state;
        return state.withConnection(true, fn, options);
    }
    async writeLock(fn, options) {
        const state = await this.state;
        return state.withConnection(false, fn, options);
    }
    async refreshSchema() {
        const state = await this.state;
        await state.refreshSchema();
    }
    registerListener(listener) {
        if (this.resolvedWriter) {
            return this.resolvedWriter.registerListener(listener);
        }
        else {
            const pending = { listener };
            this.pendingListeners.add(pending);
            return () => {
                if (pending.closeAfterRegisteredOnResolvedPool) {
                    return pending.closeAfterRegisteredOnResolvedPool();
                }
                else {
                    // Has not been registered yet, we can just remove the pending listener.
                    this.pendingListeners.delete(pending);
                }
            };
        }
    }
    async shareConnection() {
        const state = await this.state;
        return state.writer.shareConnection();
    }
    getConfiguration() {
        if (this.resolvedWriter) {
            return this.resolvedWriter.getConfiguration();
        }
        throw new Error('AsyncDbAdapter.getConfiguration() can only be called after initializing it.');
    }
}
function singleConnectionPoolState(connection) {
    return {
        writer: connection,
        withConnection: (allowReadOnly, fn, options) => {
            if (allowReadOnly) {
                return connection.readLock(fn, options);
            }
            else {
                return connection.writeLock(fn, options);
            }
        },
        close: () => connection.close(),
        refreshSchema: () => connection.refreshSchema()
    };
}
function readWritePoolState(writer, readers) {
    // DatabaseClients have locks internally, so these aren't necessary for correctness. However, our mutex and semaphore
    // implementations are very cheap to cancel, which we use to dispatch reads to the first available connection (by
    // simply requesting all of them and sticking with the first connection we get).
    const writerMutex = new Mutex();
    const readerSemaphore = new Semaphore(readers);
    return {
        writer,
        async withConnection(allowReadOnly, fn, options) {
            return acquireFromPool(writerMutex, writer, readerSemaphore, (connection) => {
                return allowReadOnly ? connection.readLock(fn) : connection.writeLock(fn);
            }, options, allowReadOnly);
        },
        async close() {
            await writer.close();
            await Promise.all(readers.map((r) => r.close()));
        },
        async refreshSchema() {
            await writer.refreshSchema();
            await Promise.all(readers.map((r) => r.refreshSchema()));
        }
    };
}
//# sourceMappingURL=AsyncWebAdapter.js.map